/**
 * Utility for exporting data from UI grids/tables to CSV format
 */

import { exportToCSV, ColumnDefinition, CSVExportOptions } from './csvExporter';

/**
 * Interface for kendo-grid like components
 */
export interface KendoGrid {
	dataSource: any;
	columns: any[];
	getOptions(): any;
	tbody?: HTMLElement;
	table?: HTMLElement;
}

/**
 * Export data from a Kendo UI Grid to CSV
 * @param grid The Kendo UI Grid component
 * @param options Export options
 */
export function exportKendoGridToCSV(
	grid: KendoGrid,
	options: CSVExportOptions = {}
): void {
	const dataSource = grid.dataSource;
	let data;
	const selectedRows = Array.from((grid as any).content.get(0).querySelectorAll(`td > input:checked`))
		.map((checkbox: any) => (grid as any).dataItem(checkbox.closest('tr')).toJSON());

	if (selectedRows?.length) {
		data = selectedRows;
	} else {
		if (dataSource.sort && dataSource.sort().length > 0 && typeof dataSource.view === 'function') {
			data = dataSource.view();
		} else {
			data = dataSource.data ? dataSource.data() : [];
		}
	}

	// Get columns from the grid
	const gridOptions = grid.getOptions();
	const columns = gridOptions.columns || grid.columns;

	// Filter out hidden columns
	const visibleColumns = columns.filter((col: any) => {
		return col.hidden !== true;
	});

	// Map to column definitions
	const columnDefinitions: ColumnDefinition[] = visibleColumns.map((col: any) => ({
		field: col.field,
		title: col.title || col.field,
		format: col.format ? (value: any) => kendo.toString(value, col.format) : undefined
	}));

	// Filter out excluded columns
	const filteredColumns = options.excludeColumns?.length
		? columnDefinitions.filter(col => !options.excludeColumns!.includes(col.field))
		: columnDefinitions;

	// Export to CSV
	exportToCSV(data, filteredColumns, options);
}

/**
 * Export data from a remote URL that returns CSV data
 * @param url The URL to fetch CSV data from
 * @param fileName Optional filename for the downloaded file
 */
export function exportFromRemoteURL(url: string, fileName?: string): void {
	fetch(url)
		.then(response => response.blob())
		.then(blob => {
			const downloadLink = document.createElement('a');
			const objectUrl = URL.createObjectURL(blob);

			downloadLink.href = objectUrl;
			downloadLink.download = fileName || 'export.csv';
			downloadLink.style.display = 'none';

			document.body.appendChild(downloadLink);
			downloadLink.click();

			setTimeout(() => {
				document.body.removeChild(downloadLink);
				URL.revokeObjectURL(objectUrl);
			}, 200);
		})
		.catch(error => {
			console.error('Export failed:', error);
		});
}

/**
 * Extract data from DOM table element and export to CSV
 * @param tableSelector CSS selector for the table element
 * @param options Export options
 */
export function exportDOMTableToCSV(
	tableSelector: string,
	options: CSVExportOptions = {}
): void {
	const table = document.querySelector(tableSelector) as HTMLTableElement;
	if (!table) {
		console.error(`Table not found: ${tableSelector}`);
		return;
	}

	const rows = Array.from(table.querySelectorAll('tr'));
	if (rows.length === 0) {
		console.error('No rows found in table');
		return;
	}

	// Get headers from first row
	const headerCells = rows[0].querySelectorAll('th');
	const headers = Array.from(headerCells).map(cell => cell.textContent?.trim() || '');

	// Get data from remaining rows
	const dataRows = rows.slice(1);
	const data = dataRows.map(row => {
		const cells = row.querySelectorAll('td');
		return Array.from(cells).map((cell, index) => {
			return {
				[headers[index] || `Column${index+1}`]: cell.textContent?.trim() || ''
			};
		}).reduce((obj, item) => ({ ...obj, ...item }), {});
	});

	// Create column definitions
	const columns: ColumnDefinition[] = headers.map(header => ({
		field: header || '',
		title: header || ''
	}));

	// Export to CSV
	exportToCSV(data, columns, options);
}
